import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, Badge, Field, SectionTitle } from "@/components/ui-hud";
import { Modal } from "@/pages/Contracts";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, MessageSquare, ShieldCheck, ArrowLeft } from "lucide-react";
import BackToCockpit from "@/components/BackToCockpit";

const CATS = ["general", "training", "aog", "operators", "safety", "evtol", "charter"];

export default function Forum() {
  const { user } = useAuth();
  const [topics, setTopics] = useState([]);
  const [cat, setCat] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", category: "general", body: "" });

  const load = () => {
    const url = cat === "all" ? "/forum/topics" : `/forum/topics?category=${cat}`;
    api.get(url).then((r) => setTopics(r.data));
  };
  useEffect(() => { load(); }, [cat]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/forum/topics", form);
      toast.success("Topic posted");
      setOpen(false);
      setForm({ title: "", category: "general", body: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <BackToCockpit />
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <SectionTitle overline="COMMUNITY FORUM" title="The pilot lounge." subtitle="26,500 members. Real questions, real answers, verified badges." />
        {user && (
          <button data-testid="btn-post-topic" onClick={() => setOpen(true)} className="btn-hud btn-hud-solid">
            <Plus className="h-3.5 w-3.5" /> NEW TOPIC
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {["all", ...CATS].map((c) => (
          <button key={c} data-testid={`forum-cat-${c}`} onClick={() => setCat(c)} className={`btn-hud ${cat === c ? 'btn-hud-solid' : ''}`}>{c.toUpperCase()}</button>
        ))}
      </div>

      <div className="space-y-3 stagger">
        {topics.map((t) => (
          <Link key={t.id} to={`/forum/${t.id}`} data-testid={`topic-${t.id}`} className="block">
            <Card className="hover:border-cyan/40 transition-colors">
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <Badge tone="cyan">{t.category.toUpperCase()}</Badge>
                    <div className="overline">{t.author_name}</div>
                    {t.faa_verified && <div className="overline text-avail flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> FAA VERIFIED</div>}
                  </div>
                  <h3 className="font-display text-xl font-semibold tracking-tight">{t.title}</h3>
                  <p className="text-sm text-slate-400 mt-2 line-clamp-2">{t.body}</p>
                </div>
                <div className="text-right">
                  <div className="font-mono text-2xl text-cyan">{t.reply_count || 0}</div>
                  <div className="overline flex items-center gap-1"><MessageSquare className="h-3 w-3" /> REPLIES</div>
                </div>
              </div>
            </Card>
          </Link>
        ))}
        {topics.length === 0 && <div className="text-slate-500 overline">NO TOPICS YET</div>}
      </div>

      {open && (
        <Modal onClose={() => setOpen(false)}>
          <h2 className="font-display text-2xl font-bold mb-6">New Topic</h2>
          <form onSubmit={submit} className="space-y-4">
            <Field label="Title"><input data-testid="input-topic-title" required className="input-hud" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
            <Field label="Category">
              <select data-testid="input-topic-cat" className="input-hud" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Body"><textarea data-testid="input-topic-body" required rows={6} className="input-hud" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} /></Field>
            <button data-testid="btn-submit-topic" className="btn-hud btn-hud-solid w-full justify-center">PUBLISH</button>
          </form>
        </Modal>
      )}
    </div>
  );
}

export function TopicView() {
  const { tid } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [reply, setReply] = useState("");

  const load = () => api.get(`/forum/topics/${tid}`).then((r) => setData(r.data));
  useEffect(() => { load(); }, [tid]);

  const submitReply = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/forum/topics/${tid}/reply`, { body: reply });
      setReply("");
      toast.success("Reply posted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Sign in to reply"); }
  };

  if (!data) return <div className="max-w-4xl mx-auto p-12 overline">LOADING</div>;

  return (
    <div className="max-w-4xl mx-auto px-6 py-12">
      <Link to="/forum" data-testid="back-forum" className="overline text-cyan mb-6 inline-flex items-center gap-2"><ArrowLeft className="h-3 w-3" /> BACK TO FORUM</Link>
      <Card>
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <Badge tone="cyan">{data.topic.category.toUpperCase()}</Badge>
          <div className="overline">{data.topic.author_name}</div>
          {data.topic.faa_verified && <div className="overline text-avail flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> FAA VERIFIED</div>}
        </div>
        <h1 className="font-display text-3xl font-bold tracking-tight mb-4">{data.topic.title}</h1>
        <p className="text-slate-300 whitespace-pre-wrap leading-relaxed">{data.topic.body}</p>
      </Card>

      <div className="mt-8 mb-4 overline text-cyan">// {data.replies.length} REPLIES</div>
      <div className="space-y-3">
        {data.replies.map((r) => (
          <Card key={r.id} testId={`reply-${r.id}`}>
            <div className="flex items-center gap-3 mb-2">
              <div className="overline">{r.author_name}</div>
              {r.faa_verified && <div className="overline text-avail flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> VERIFIED</div>}
            </div>
            <p className="text-slate-300 whitespace-pre-wrap">{r.body}</p>
          </Card>
        ))}
      </div>

      {user && (
        <form onSubmit={submitReply} className="mt-8 space-y-3">
          <Field label="Add a Reply">
            <textarea data-testid="input-reply" required rows={4} className="input-hud" value={reply} onChange={(e) => setReply(e.target.value)} />
          </Field>
          <button data-testid="btn-submit-reply" className="btn-hud btn-hud-solid">POST REPLY</button>
        </form>
      )}
    </div>
  );
}
